import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import { FloatingNav, OverlaySideNav, HeroSection as LayoutHeroSection, NavSection } from '@/components/layout';
import {
  PillarCalculator,
  ScenarioPresets,
  EligibilitySection,
  DataTable,
  DataTableHeader,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  HighlightValue,
} from '@/components/equip';
import { getPhysicianEligibility } from '@/data';
import type { EmploymentStatus } from '@/data/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import {
  Scale,
  Target,
  Users,
  ChevronRight,
  Book,
  HelpCircle,
  Calculator,
  DollarSign,
  Calendar,
  TrendingUp,
  Shield,
  Info,
  UserCheck,
  Unlock,
  CheckCircle,
  ArrowRight,
  Key,
} from 'lucide-react';

// Navigation sections - reordered for smart-brevity (Who Qualifies first)
const navSections: NavSection[] = [
  { id: 'eligibility', label: 'Who Qualifies', icon: <UserCheck className="w-4 h-4" /> },
  { id: 'how-to-earn', label: 'How to Earn', icon: <Key className="w-4 h-4" /> },
  { id: 'eligibility-measure', label: 'Eligibility Measure', icon: <Shield className="w-4 h-4" /> },
  { id: 'consistency-measures', label: 'Consistency Measures', icon: <CheckCircle className="w-4 h-4" /> },
  { id: 'outcome-measures', label: 'Outcome Measures', icon: <Target className="w-4 h-4" /> },
  { id: 'maximum-award', label: 'Maximum Award', icon: <DollarSign className="w-4 h-4" /> },
  { id: 'quarterly-breakdown', label: 'Quarterly Breakdown', icon: <Calendar className="w-4 h-4" /> },
  { id: 'interactive-calculator', label: 'Calculator', icon: <Calculator className="w-4 h-4" /> },
  { id: 'payout-scenarios', label: 'Scenarios', icon: <TrendingUp className="w-4 h-4" /> },
  { id: 'measure-definitions', label: 'Measure Definitions', icon: <Book className="w-4 h-4" /> },
  { id: 'key-messages', label: 'Key Messages', icon: <Info className="w-4 h-4" /> },
  { id: 'next-steps', label: 'Next Steps', icon: <ChevronRight className="w-4 h-4" /> },
];

// Animation variants for fade-in on scroll
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

// Section wrapper with scroll margin
function Section({
  id,
  children,
  className,
  background = 'white',
}: {
  id: string;
  children: React.ReactNode;
  className?: string;
  background?: 'white' | 'gray';
}): React.ReactElement {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <section
      id={id}
      ref={ref}
      className={cn(
        'py-12 md:py-16 scroll-mt-20',
        background === 'gray' ? 'bg-gray-50' : 'bg-white',
        className
      )}
    >
      <motion.div
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
        variants={fadeInUp}
        className="max-w-6xl mx-auto px-6"
      >
        {children}
      </motion.div>
    </section>
  );
}

// TIER 1: The 30-Second Scan - How to Earn Section (NEW)
function HowToEarnSection(): React.ReactElement {
  return (
    <Section id="how-to-earn">
      <div className="text-center mb-8">
        <Badge className="bg-eventus-purple/10 text-eventus-purple mb-4">The Big Picture</Badge>
        <h2 className="text-3xl md:text-4xl font-bold text-eventus-navy mb-4">
          How to Earn Your Incentive
        </h2>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Meet the eligibility measure. Then each earning measure you meet adds to your award.
        </p>
      </div>

      {/* Visual Diagram: Eligibility -> Earning Measures */}
      <Card className="mb-8 border-2 border-eventus-purple/20">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
            {/* Eligibility Measure Box */}
            <div className="bg-eventus-purple/10 rounded-lg p-6 text-center min-w-[200px]">
              <Key className="w-8 h-8 text-eventus-purple mx-auto mb-2" />
              <h3 className="font-bold text-eventus-navy">Eligibility Measure</h3>
              <p className="text-sm text-gray-600">ACO Visits</p>
              <p className="text-lg font-semibold text-eventus-purple mt-2">See quarterly schedule</p>
            </div>

            {/* Arrow */}
            <div className="flex items-center">
              <ArrowRight className="w-8 h-8 text-eventus-purple" />
              <span className="text-sm text-gray-500 ml-2">unlocks</span>
            </div>

            {/* Earning Measures Box */}
            <div className="bg-green-50 rounded-lg p-6 text-center min-w-[280px]">
              <Unlock className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <h3 className="font-bold text-eventus-navy">Earning Measures</h3>
              <div className="flex gap-4 justify-center mt-2">
                <div>
                  <p className="text-sm text-gray-600">Consistency</p>
                  <p className="font-semibold">3 measures</p>
                </div>
                <div className="border-l pl-4">
                  <p className="text-sm text-gray-600">Outcomes</p>
                  <p className="font-semibold">Q2+ only</p>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-2">Quarterly targets</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Three Things to Know */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-eventus-purple">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <span className="bg-eventus-purple text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
              <div>
                <p className="font-semibold text-eventus-navy">Meet the eligibility measure</p>
                <p className="text-sm text-gray-600">ACO Visits unlocks any award amount</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <span className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
              <div>
                <p className="font-semibold text-eventus-navy">Each measure adds up</p>
                <p className="text-sm text-gray-600">Meet a measure, earn that portion</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
              <div>
                <p className="font-semibold text-eventus-navy">One measure unmet, lose only that portion</p>
                <p className="text-sm text-gray-600">Other measures stay intact</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Section>
  );
}

// TIER 2: Eligibility Measure Section (simplified replacement for AcoVisitsGateSection)
function EligibilityMeasureSection(): React.ReactElement {
  return (
    <Section id="eligibility-measure" background="gray">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        The Eligibility Measure: ACO Visits
      </h2>
      <p className="text-gray-600 mb-6 text-lg">
        You must meet this measure to qualify for any award amount that quarter.
        Most physicians already exceed this through regular patient care.
      </p>

      {/* Quarter-over-quarter ramp */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <Card className="border-4 border-gray-300 hover:border-amber-400 bg-amber-50 shadow-lg">
          <CardContent className="p-8 text-center">
            <Badge className="bg-amber-500 text-white mb-3">Q1 Only</Badge>
            <p className="text-2xl font-semibold text-amber-800 mb-2">Ramp-Up Quarter</p>
            <p className="text-sm text-amber-700 mt-2">Lower target to help you settle in</p>
          </CardContent>
        </Card>
        <Card className="border-4 border-gray-300 hover:border-eventus-purple bg-eventus-purple/5 shadow-lg">
          <CardContent className="p-8 text-center">
            <Badge className="bg-eventus-purple text-white mb-3">Q2, Q3, Q4</Badge>
            <p className="text-2xl font-semibold text-eventus-navy mb-2">Standard Quarters</p>
            <p className="text-sm text-gray-600 mt-2">Standard target for the rest of the year</p>
          </CardContent>
        </Card>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-amber-800">
          <strong>If you don't meet the target:</strong> That quarter's award is $0.
          Each quarter is independent—next quarter is a fresh start.
        </p>
      </div>
    </Section>
  );
}

// TIER 2: Consistency Measures Section (NEW)
function ConsistencyMeasuresSection(): React.ReactElement {
  return (
    <Section id="consistency-measures">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Consistency Measures
      </h2>
      <p className="text-gray-600 mb-6 text-lg">
        Three measures available every quarter. Meet the target for each to earn that portion of your award.
      </p>

      <div className="grid md:grid-cols-3 gap-4">
        {[
          { name: 'PIMs', desc: 'Medication Safety' },
          { name: 'Coding Accuracy', desc: 'Diagnosis coding' },
          { name: 'Signature Standards', desc: 'Documentation quality' }
        ].map((measure) => (
          <Card key={measure.name} className="border-2">
            <CardContent className="p-4 text-center">
              <h3 className="font-semibold text-eventus-navy text-lg">{measure.name}</h3>
              <p className="text-sm text-gray-500 mb-2">{measure.desc}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </Section>
  );
}

// TIER 2: Outcome Measures Section (NEW)
function OutcomeMeasuresSection(): React.ReactElement {
  const outcomeMeasures = [
    { name: 'A1C', desc: 'Diabetes', quarters: 'Q2-Q4' },
    { name: 'BCS', desc: 'Breast Cancer', quarters: 'Q2-Q4' },
    { name: 'Colorectal', desc: 'Cancer Screening', quarters: 'Q2-Q4' },
    { name: 'PHQ-9', desc: 'Depression', quarters: 'Q3-Q4' },
    { name: 'HTN', desc: 'Hypertension', quarters: 'Q4 only' },
  ];

  return (
    <Section id="outcome-measures" background="gray">
      <Badge className="bg-blue-100 text-blue-700 mb-4">Starts Q2</Badge>
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Outcome Measures
      </h2>
      <p className="text-gray-600 mb-6 text-lg">
        Clinical quality measures worth 60% of your award in Q2-Q4. Targets graduate across the year to give you time to build performance.
      </p>

      {/* The 5 Outcome Measures */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {outcomeMeasures.map((measure) => (
          <Card key={measure.name} className="border-2">
            <CardContent className="p-3 text-center">
              <h3 className="font-bold text-eventus-navy">{measure.name}</h3>
              <p className="text-xs text-gray-500">{measure.desc}</p>
              <Badge variant="outline" className="mt-2 text-xs">{measure.quarters}</Badge>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Weight Distribution by Quarter */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <p className="font-semibold text-eventus-navy mb-3">Weight per measure (60% total divided equally):</p>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="text-center bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-500">Q2</p>
              <p className="text-lg font-bold text-eventus-navy">4 measures</p>
              <p className="text-eventus-purple font-semibold">15% each</p>
            </div>
            <div className="text-center bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-500">Q3</p>
              <p className="text-lg font-bold text-eventus-navy">5 measures</p>
              <p className="text-eventus-purple font-semibold">12% each</p>
            </div>
            <div className="text-center bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-500">Q4</p>
              <p className="text-lg font-bold text-eventus-navy">6 measures</p>
              <p className="text-eventus-purple font-semibold">10% each</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-blue-800 text-sm">
            <strong>Outcome-measure targets graduate across the year</strong> — lower in Q2, higher by Q4. Meet the quarterly target → earn that measure's portion.
            Unmet → only lose that portion (other measures unaffected).
          </p>
        </CardContent>
      </Card>
    </Section>
  );
}

// Maximum Award Section
function MaximumAwardSection({ employmentStatus }: { employmentStatus?: EmploymentStatus }): React.ReactElement {
  const isPartTime = employmentStatus === 'part-time';
  const isFullTime = employmentStatus === 'full-time';
  const [daysPerWeek, setDaysPerWeek] = useState(3);
  const partTimeAward = daysPerWeek * 800;

  return (
    <Section id="maximum-award">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Your Maximum Award
      </h2>
      <p className="text-gray-600 mb-6">
        {isPartTime
          ? 'Your award ceiling scales with your schedule. Use the slider to see your maximum.'
          : isFullTime
            ? 'Performance determines how much of this ceiling you earn each quarter.'
            : 'Employment status determines the ceiling. Performance determines how much of that ceiling is earned.'}
      </p>

      {/* Full-Time: Hero display */}
      {isFullTime && (
        <Card className="border-2 border-eventus-purple/20 mb-6">
          <CardContent className="py-8">
            <div className="flex items-center justify-center gap-8">
              <div className="text-center">
                <div className="text-5xl font-bold text-eventus-navy">$4,000</div>
                <div className="text-gray-500 mt-2">per quarter</div>
              </div>
              <div className="hidden md:block w-px h-16 bg-gray-200" />
              <div className="hidden md:block text-center">
                <div className="text-3xl font-bold text-eventus-navy/60">$16,000</div>
                <div className="text-gray-400 mt-2">annual potential</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Part-Time: Calculator only */}
      {isPartTime && (
        <Card className="border-2 mb-6">
          <CardContent className="p-6 space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-600 font-medium">Days worked per week:</span>
                <span className="font-bold text-eventus-purple text-xl">{daysPerWeek}</span>
              </div>
              <Slider
                value={[daysPerWeek]}
                onValueChange={([v]) => setDaysPerWeek(v)}
                min={1}
                max={4}
                step={1}
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>1</span>
                <span>2</span>
                <span>3</span>
                <span>4</span>
              </div>
            </div>

            <div className="bg-eventus-purple/5 rounded-xl p-6 text-center">
              <div className="text-5xl font-bold text-eventus-navy">
                ${partTimeAward.toLocaleString()}
              </div>
              <div className="text-gray-500 mt-2">per quarter &middot; $800 &times; {daysPerWeek} days</div>
              <div className="text-gray-400 text-sm mt-1">${(partTimeAward * 4).toLocaleString()} annual potential</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin/leadership view: full table */}
      {!isFullTime && !isPartTime && (
        <DataTable className="mb-6">
          <DataTableHeader>
            <DataTableRow>
              <DataTableCell header>Employment</DataTableCell>
              <DataTableCell header align="center">Days/Week</DataTableCell>
              <DataTableCell header align="right">Maximum Award</DataTableCell>
            </DataTableRow>
          </DataTableHeader>
          <DataTableBody>
            <DataTableRow highlight>
              <DataTableCell className="font-medium">Full-Time</DataTableCell>
              <DataTableCell align="center">—</DataTableCell>
              <DataTableCell align="right">
                <HighlightValue>$4,000</HighlightValue>
              </DataTableCell>
            </DataTableRow>
            <DataTableRow>
              <DataTableCell className="font-medium">Part-Time</DataTableCell>
              <DataTableCell align="center">4</DataTableCell>
              <DataTableCell align="right">$3,200</DataTableCell>
            </DataTableRow>
            <DataTableRow>
              <DataTableCell className="font-medium">Part-Time</DataTableCell>
              <DataTableCell align="center">3</DataTableCell>
              <DataTableCell align="right">$2,400</DataTableCell>
            </DataTableRow>
            <DataTableRow>
              <DataTableCell className="font-medium">Part-Time</DataTableCell>
              <DataTableCell align="center">2</DataTableCell>
              <DataTableCell align="right">$1,600</DataTableCell>
            </DataTableRow>
            <DataTableRow>
              <DataTableCell className="font-medium">Part-Time</DataTableCell>
              <DataTableCell align="center">1</DataTableCell>
              <DataTableCell align="right">$800</DataTableCell>
            </DataTableRow>
          </DataTableBody>
        </DataTable>
      )}

      {/* Schedule ceiling note removed per COO feedback */}
    </Section>
  );
}

// Quarterly Breakdown Section - Visual comparison of Q1 vs Q2-Q4 calculations
function QuarterlyBreakdownSection(): React.ReactElement {
  return (
    <Section id="quarterly-breakdown" background="gray">
      <div className="text-center mb-8">
        <Badge className="bg-eventus-purple/10 text-eventus-purple mb-4">How It All Fits Together</Badge>
        <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
          Quarterly Calculation Breakdown
        </h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Q1 has a different structure than Q2-Q4. Here's how your award is calculated each quarter.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Q1 Column */}
        <Card className="border-4 border-gray-300 hover:border-amber-400 overflow-hidden">
          <div className="bg-amber-400 text-white p-4 text-center">
            <h3 className="text-2xl font-bold">Q1</h3>
            <p className="text-amber-100">Foundation Quarter</p>
          </div>
          <CardContent className="p-6 space-y-4">
            {/* Eligibility Measure */}
            <div className="bg-amber-50 rounded-lg p-4 border-2 border-amber-200">
              <div className="flex items-center gap-2 mb-2">
                <Key className="w-5 h-5 text-amber-600" />
                <span className="font-bold text-amber-800">Eligibility Measure</span>
              </div>
              <div className="text-center py-3">
                <p className="text-sm text-gray-500">ACO Visits</p>
                <p className="text-lg font-semibold text-amber-700">Ramp-up target</p>
                <p className="text-xs text-amber-700 mt-1">Must meet to qualify for any award</p>
              </div>
            </div>

            {/* Arrow */}
            <div className="text-center">
              <ArrowRight className="w-6 h-6 text-amber-400 mx-auto rotate-90" />
              <span className="text-xs text-gray-500">If met, earn from:</span>
            </div>

            {/* Consistency Measures */}
            <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-blue-600" />
                <span className="font-bold text-blue-800">Consistency Measures</span>
                <Badge className="bg-blue-600 text-white ml-auto">100%</Badge>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                <div className="text-center bg-white rounded p-2">
                  <p className="text-xs font-medium">PIMs</p>
                  <p className="text-sm font-bold text-blue-600">33%</p>
                </div>
                <div className="text-center bg-white rounded p-2">
                  <p className="text-xs font-medium">Coding</p>
                  <p className="text-sm font-bold text-blue-600">33%</p>
                </div>
                <div className="text-center bg-white rounded p-2">
                  <p className="text-xs font-medium">Signature</p>
                  <p className="text-sm font-bold text-blue-600">33%</p>
                </div>
              </div>
            </div>

            {/* Outcome Measures - Not Available */}
            <div className="bg-gray-100 rounded-lg p-4 border-2 border-gray-200 opacity-50">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-gray-400" />
                <span className="font-bold text-gray-500">Outcome Measures</span>
                <Badge variant="outline" className="ml-auto text-gray-400 border-gray-300">Not Active</Badge>
              </div>
              <p className="text-xs text-gray-400 mt-2 text-center">Outcomes begin in Q2</p>
            </div>

            {/* Max Award */}
            <div className="bg-eventus-navy text-white rounded-lg p-4 text-center">
              <p className="text-sm text-gray-300">Maximum Award</p>
              <p className="text-3xl font-bold text-green-400">$4,000</p>
            </div>
          </CardContent>
        </Card>

        {/* Q2-Q4 Column */}
        <Card className="border-4 border-gray-300 hover:border-eventus-purple overflow-hidden">
          <div className="bg-eventus-purple text-white p-4 text-center">
            <h3 className="text-2xl font-bold">Q2, Q3, Q4</h3>
            <p className="text-purple-200">Full Program</p>
          </div>
          <CardContent className="p-6 space-y-4">
            {/* Eligibility Measure */}
            <div className="bg-eventus-purple/10 rounded-lg p-4 border-2 border-eventus-purple/30">
              <div className="flex items-center gap-2 mb-2">
                <Key className="w-5 h-5 text-eventus-purple" />
                <span className="font-bold text-eventus-navy">Eligibility Measure</span>
              </div>
              <div className="text-center py-3">
                <p className="text-sm text-gray-500">ACO Visits</p>
                <p className="text-lg font-semibold text-eventus-purple">Standard target</p>
                <p className="text-xs text-gray-600 mt-1">Must meet to qualify for any award</p>
              </div>
            </div>

            {/* Arrow */}
            <div className="text-center">
              <ArrowRight className="w-6 h-6 text-eventus-purple mx-auto rotate-90" />
              <span className="text-xs text-gray-500">If met, earn from:</span>
            </div>

            {/* Outcome Measures */}
            <div className="bg-pink-50 rounded-lg p-4 border-2 border-pink-200">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-5 h-5 text-pink-600" />
                <span className="font-bold text-pink-800">Outcome Measures</span>
                <Badge className="bg-pink-600 text-white ml-auto">60%</Badge>
              </div>
              <div className="grid grid-cols-5 gap-1 mt-3">
                {['A1C', 'CRCS', 'PHQ-9', 'BCS', 'HTN'].map((m) => (
                  <div key={m} className="text-center bg-white rounded p-1">
                    <p className="text-xs font-medium">{m}</p>
                    <p className="text-xs font-bold text-pink-600">12%</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center">Each measure: 60% ÷ active measures</p>
            </div>

            {/* Consistency Measures */}
            <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-blue-600" />
                <span className="font-bold text-blue-800">Consistency Measures</span>
                <Badge className="bg-blue-600 text-white ml-auto">40%</Badge>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                <div className="text-center bg-white rounded p-2">
                  <p className="text-xs font-medium">PIMs</p>
                  <p className="text-sm font-bold text-blue-600">13%</p>
                </div>
                <div className="text-center bg-white rounded p-2">
                  <p className="text-xs font-medium">Coding</p>
                  <p className="text-sm font-bold text-blue-600">13%</p>
                </div>
                <div className="text-center bg-white rounded p-2">
                  <p className="text-xs font-medium">Signature</p>
                  <p className="text-sm font-bold text-blue-600">13%</p>
                </div>
              </div>
            </div>

            {/* Max Award */}
            <div className="bg-eventus-navy text-white rounded-lg p-4 text-center">
              <p className="text-sm text-gray-300">Maximum Award</p>
              <p className="text-3xl font-bold text-green-400">$4,000</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Key Takeaway */}
      <Card className="mt-8 bg-eventus-navy text-white">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <Info className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-bold text-lg mb-2 text-white">Key Takeaway</h4>
              <p className="text-gray-300">
                <strong className="text-white">Q1 is simpler:</strong> Meet the ACO Visits eligibility target, then earn from 3 consistency measures.
                <br />
                <strong className="text-white">Q2-Q4 adds outcomes:</strong> Meet the ACO Visits eligibility target, then earn from both outcome measures (60% of award) and consistency measures (40%).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </Section>
  );
}

// Interactive Calculator Section
function InteractiveCalculatorSection({ readOnly = false, lockedCalculatorRole, employmentStatus }: { readOnly?: boolean; lockedCalculatorRole?: import('@/store/equipStore').RoleType; employmentStatus?: EmploymentStatus }): React.ReactElement {
  return (
    <Section id="interactive-calculator" background="gray">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-2">
        Interactive Calculator
      </h2>
      <p className="text-gray-600 mb-6">
        Practice scenarios for team discussions. Click any measure to toggle measure met/unmet.
      </p>
      <PillarCalculator track="physician" lockedRole={readOnly ? (lockedCalculatorRole ?? 'primary-care-md') : undefined} effectiveEmploymentStatus={employmentStatus} />
    </Section>
  );
}

// Hero Section using shared component
function HeroSection({ readOnly = false }: { readOnly?: boolean }): React.ReactElement {
  return (
    <LayoutHeroSection
      role="physician"
      badgeText="Physician Framework"
      title="EQuIP 2026 BETA — Physician Framework"
      description={
        readOnly
          ? 'Your EQuIP program framework — measures, awards, and how to earn.'
          : 'Reference guide for leadership. All the data you need to explain the program to your team.'
      }
      cta={{
        label: 'Jump to Calculator',
        icon: <Calculator className="w-4 h-4" />,
        onClick: () => {
          const element = document.getElementById('interactive-calculator');
          if (element) {
            const yOffset = -80;
            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
          }
        },
      }}
    />
  );
}

// TIER 3: Payout Scenarios Section
function PayoutScenariosSection({ employmentStatus }: { employmentStatus?: EmploymentStatus }): React.ReactElement {
  const scenarioDescription = employmentStatus === 'part-time'
    ? 'All scenarios assume part-time employment. Actual amounts scale with your schedule.'
    : 'All scenarios assume full-time, productivity-based employment ($4,000 max).';

  return (
    <Section id="payout-scenarios" background="gray">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Payout Scenarios
      </h2>
      <p className="text-gray-600 mb-6">
        Multiple scenarios visible at once for comparison. {scenarioDescription}
      </p>

      <Accordion type="single" collapsible className="space-y-4">
        {/* Q1 Scenarios */}
        <AccordionItem value="q1-scenarios" className="border rounded-lg">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3">
              <Badge className="bg-amber-500">Q1</Badge>
              <span className="font-semibold">Scenarios (Eligibility + Consistency Only)</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <DataTable>
              <DataTableHeader>
                <DataTableRow>
                  <DataTableCell header>Scenario</DataTableCell>
                  <DataTableCell header align="center">ACO Visits</DataTableCell>
                  <DataTableCell header align="center">Consistency</DataTableCell>
                  <DataTableCell header align="center">Total %</DataTableCell>
                  <DataTableCell header align="right">Award</DataTableCell>
                </DataTableRow>
              </DataTableHeader>
              <DataTableBody>
                <DataTableRow highlight>
                  <DataTableCell className="font-medium">Q1: Perfect Performance</DataTableCell>
                  <DataTableCell align="center">
                    <Badge className="bg-green-500">Met</Badge>
                  </DataTableCell>
                  <DataTableCell align="center" className="text-blue-600">3/3 (100%)</DataTableCell>
                  <DataTableCell align="center" className="font-semibold">100%</DataTableCell>
                  <DataTableCell align="right">
                    <HighlightValue color="green">$4,000</HighlightValue>
                  </DataTableCell>
                </DataTableRow>
                <DataTableRow>
                  <DataTableCell className="font-medium">Q1: Partial Consistency</DataTableCell>
                  <DataTableCell align="center">
                    <Badge className="bg-green-500">Met</Badge>
                  </DataTableCell>
                  <DataTableCell align="center" className="text-blue-600">2/3 (66.7%)</DataTableCell>
                  <DataTableCell align="center" className="font-semibold">66.7%</DataTableCell>
                  <DataTableCell align="right">
                    <HighlightValue color="purple">$2,667</HighlightValue>
                  </DataTableCell>
                </DataTableRow>
                <DataTableRow>
                  <DataTableCell className="font-medium">Q1: One Consistency Met</DataTableCell>
                  <DataTableCell align="center">
                    <Badge className="bg-green-500">Met</Badge>
                  </DataTableCell>
                  <DataTableCell align="center" className="text-blue-600">1/3 (33.3%)</DataTableCell>
                  <DataTableCell align="center" className="font-semibold">33.3%</DataTableCell>
                  <DataTableCell align="right">
                    <HighlightValue color="purple">$1,333</HighlightValue>
                  </DataTableCell>
                </DataTableRow>
                <DataTableRow>
                  <DataTableCell className="font-medium">
                    Q1: Eligibility Unmet
                    <span className="text-xs text-gray-500 ml-2">(Incentive paused)</span>
                  </DataTableCell>
                  <DataTableCell align="center">
                    <Badge variant="outline" className="text-gray-500 border-gray-400">Unmet</Badge>
                  </DataTableCell>
                  <DataTableCell align="center" className="text-gray-400">-</DataTableCell>
                  <DataTableCell align="center" className="text-gray-400">-</DataTableCell>
                  <DataTableCell align="right">
                    <span className="text-gray-400">Paused</span>
                  </DataTableCell>
                </DataTableRow>
              </DataTableBody>
            </DataTable>
          </AccordionContent>
        </AccordionItem>

        {/* Q2+ Scenarios */}
        <AccordionItem value="q2-scenarios" className="border rounded-lg">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3">
              <Badge className="bg-green-500">Q2-Q4</Badge>
              <span className="font-semibold">Scenarios (Eligibility + Outcomes + Consistency)</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <DataTable>
              <DataTableHeader>
                <DataTableRow>
                  <DataTableCell header>Scenario</DataTableCell>
                  <DataTableCell header align="center">ACO Visits</DataTableCell>
                  <DataTableCell header align="center">Outcomes</DataTableCell>
                  <DataTableCell header align="center">Consistency</DataTableCell>
                  <DataTableCell header align="center">Total %</DataTableCell>
                  <DataTableCell header align="right">Award</DataTableCell>
                </DataTableRow>
              </DataTableHeader>
              <DataTableBody>
                <DataTableRow highlight>
                  <DataTableCell className="font-medium">Perfect Q4</DataTableCell>
                  <DataTableCell align="center">
                    <Badge className="bg-green-500">Met</Badge>
                  </DataTableCell>
                  <DataTableCell align="center" className="text-pink-600">6/6 (60%)</DataTableCell>
                  <DataTableCell align="center" className="text-blue-600">3/3 (40%)</DataTableCell>
                  <DataTableCell align="center" className="font-semibold">100%</DataTableCell>
                  <DataTableCell align="right">
                    <HighlightValue color="green">$4,000</HighlightValue>
                  </DataTableCell>
                </DataTableRow>
                <DataTableRow>
                  <DataTableCell className="font-medium">One Outcome Unmet</DataTableCell>
                  <DataTableCell align="center">
                    <Badge className="bg-green-500">Met</Badge>
                  </DataTableCell>
                  <DataTableCell align="center" className="text-pink-600">5/6 (50%)</DataTableCell>
                  <DataTableCell align="center" className="text-blue-600">3/3 (40%)</DataTableCell>
                  <DataTableCell align="center" className="font-semibold">90%</DataTableCell>
                  <DataTableCell align="right">
                    <HighlightValue color="purple">$3,600</HighlightValue>
                  </DataTableCell>
                </DataTableRow>
                <DataTableRow>
                  <DataTableCell className="font-medium">Two Outcomes Unmet</DataTableCell>
                  <DataTableCell align="center">
                    <Badge className="bg-green-500">Met</Badge>
                  </DataTableCell>
                  <DataTableCell align="center" className="text-pink-600">4/6 (40%)</DataTableCell>
                  <DataTableCell align="center" className="text-blue-600">3/3 (40%)</DataTableCell>
                  <DataTableCell align="center" className="font-semibold">80%</DataTableCell>
                  <DataTableCell align="right">
                    <HighlightValue color="purple">$3,200</HighlightValue>
                  </DataTableCell>
                </DataTableRow>
                <DataTableRow>
                  <DataTableCell className="font-medium">Consistency Struggle</DataTableCell>
                  <DataTableCell align="center">
                    <Badge className="bg-green-500">Met</Badge>
                  </DataTableCell>
                  <DataTableCell align="center" className="text-pink-600">6/6 (60%)</DataTableCell>
                  <DataTableCell align="center" className="text-blue-600">1/3 (13.3%)</DataTableCell>
                  <DataTableCell align="center" className="font-semibold">73.3%</DataTableCell>
                  <DataTableCell align="right">
                    <HighlightValue color="purple">$2,933</HighlightValue>
                  </DataTableCell>
                </DataTableRow>
                <DataTableRow>
                  <DataTableCell className="font-medium">
                    All Outcomes Unmet
                    <span className="text-xs text-blue-600 ml-2">(Consistency floor)</span>
                  </DataTableCell>
                  <DataTableCell align="center">
                    <Badge className="bg-green-500">Met</Badge>
                  </DataTableCell>
                  <DataTableCell align="center" className="text-pink-600">0/6 (0%)</DataTableCell>
                  <DataTableCell align="center" className="text-blue-600">3/3 (40%)</DataTableCell>
                  <DataTableCell align="center" className="font-semibold">40%</DataTableCell>
                  <DataTableCell align="right">
                    <HighlightValue color="purple">$1,600</HighlightValue>
                  </DataTableCell>
                </DataTableRow>
                <DataTableRow>
                  <DataTableCell className="font-medium">
                    Eligibility Unmet
                    <span className="text-xs text-gray-500 ml-2">(Incentive paused)</span>
                  </DataTableCell>
                  <DataTableCell align="center">
                    <Badge variant="outline" className="text-gray-500 border-gray-400">Unmet</Badge>
                  </DataTableCell>
                  <DataTableCell align="center" className="text-gray-400">-</DataTableCell>
                  <DataTableCell align="center" className="text-gray-400">-</DataTableCell>
                  <DataTableCell align="center" className="text-gray-400">-</DataTableCell>
                  <DataTableCell align="right">
                    <span className="text-gray-400">Paused</span>
                  </DataTableCell>
                </DataTableRow>
              </DataTableBody>
            </DataTable>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Key Insight */}
      <Card className="mt-6 bg-green-50 border-green-200">
        <CardContent className="p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-green-800 text-sm">
            <strong>Key insight:</strong> When you meet the ACO Visits eligibility measure, you can still earn up to 40% from Consistency measures even if you do not meet any Outcome measures. The eligibility measure opens your earning potential, and each measure stands on its own from there.
          </p>
        </CardContent>
      </Card>

      {/* Scenario Presets */}
      <div className="mt-8">
        <h3 className="text-xl font-bold text-eventus-navy mb-4">Pre-Built Scenarios</h3>
        <p className="text-gray-600 mb-4">
          Walk through these with your team to build intuition.
        </p>
        <ScenarioPresets track="physician" employmentStatus={employmentStatus} />
      </div>
    </Section>
  );
}

// TIER 3: Measure Definitions Section
function MeasureDefinitionsSection(): React.ReactElement {
  const outcomeMeasures = [
    {
      abbrev: 'A1C',
      name: 'Diabetes A1C Testing',
      description: 'A1C testing for diabetic patients',
      quarters: 'Q2-Q4',
    },
    {
      abbrev: 'BCS',
      name: 'Breast Cancer Screening',
      description: 'Mammogram completion for eligible patients',
      quarters: 'Q2-Q4',
    },
    {
      abbrev: 'Colorectal',
      name: 'Colorectal Cancer Screening',
      description: 'Colonoscopy/FIT completion for eligible patients',
      quarters: 'Q2-Q4',
    },
    {
      abbrev: 'PHQ-9',
      name: 'Depression Screening',
      description: 'Mental health screening completion',
      quarters: 'Q3-Q4',
    },
    {
      abbrev: 'HTN',
      name: 'Hypertension Control',
      description: 'Blood pressure documentation',
      quarters: 'Q4',
    },
  ];

  const consistencyMeasures = [
    {
      abbrev: 'PIMs',
      name: 'Medication Safety',
      description: 'Prescribing patterns for potentially inappropriate medications',
    },
    {
      abbrev: 'Coding',
      name: 'Coding Accuracy',
      description: 'Results from coding audits',
    },
    {
      abbrev: 'Documentation',
      name: 'Signature Standards',
      description: 'Results from quality/signature audits',
    },
  ];

  return (
    <Section id="measure-definitions">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Measure Definitions
      </h2>
      <p className="text-gray-600 mb-6">
        Complete reference for the eligibility measure and all weighted measures.
      </p>

      {/* Eligibility Measure */}
      <Card className="mb-8 border-2 border-eventus-purple/30">
        <CardHeader className="pb-2 bg-eventus-purple/5">
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="w-5 h-5 text-eventus-purple" />
            Eligibility Measure
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="font-mono bg-eventus-purple/10 border-eventus-purple/30">
                  ACO Visits
                </Badge>
                <span className="font-medium text-eventus-navy">ACO Visit Completion</span>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                Percentage of assigned ACO patients seen during the calendar year. This is your foundation for incentive eligibility.
              </p>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Q1:</span>
                  <Badge className="bg-amber-500">Ramp-up target</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Q2-Q4:</span>
                  <Badge className="bg-green-500">Standard target</Badge>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Outcome Measures Accordion */}
      <h3 className="text-lg font-semibold text-eventus-navy mb-3 flex items-center gap-2">
        <div className="w-3 h-3 rounded-full bg-pink-500" />
        Outcome Measures (60% weight after measure met, Q2+ only)
      </h3>
      <Accordion type="single" collapsible className="mb-8">
        {outcomeMeasures.map((measure) => (
          <AccordionItem key={measure.abbrev} value={measure.abbrev}>
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="font-mono">
                  {measure.abbrev}
                </Badge>
                <span className="font-medium">{measure.name}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="pl-4 space-y-2 text-sm">
                <p className="text-gray-600">{measure.description}</p>
                <div className="flex gap-4">
                  <span>
                    <strong>Quarters:</strong> {measure.quarters}
                  </span>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {/* Consistency Measures Accordion */}
      <h3 className="text-lg font-semibold text-eventus-navy mb-3 flex items-center gap-2">
        <div className="w-3 h-3 rounded-full bg-blue-500" />
        Consistency Measures (40% weight, or 100% in Q1)
      </h3>
      <Accordion type="single" collapsible>
        {consistencyMeasures.map((measure) => (
          <AccordionItem key={measure.abbrev} value={measure.abbrev}>
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="font-mono">
                  {measure.abbrev}
                </Badge>
                <span className="font-medium">{measure.name}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="pl-4 space-y-2 text-sm">
                <p className="text-gray-600">{measure.description}</p>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Section>
  );
}

// Key Messages Section
function KeyMessagesSection({ employmentStatus, isLeadership }: { employmentStatus?: EmploymentStatus; isLeadership?: boolean }): React.ReactElement {
  const baseMessages = [
    {
      title: 'Meeting ACO Visits opens your earning potential.',
      message: 'Meeting the ACO Visits measure unlocks your eligibility for the full incentive program. Once you meet this standard, you are positioned to earn based on your performance across all other measures.',
      icon: Unlock,
      color: 'green',
    },
    {
      title: 'After eligibility, each measure stands on its own.',
      message: 'After meeting your ACO Visits measure, each earning measure stands on its own. Strong performance in one area supports your success without being pulled down by another.',
      icon: Scale,
      color: 'blue',
    },
    {
      title: isLeadership ? 'Their facilities, their metrics.' : 'Your facilities, your metrics.',
      message: isLeadership
        ? 'Physicians are measured on outcomes at their assigned facilities.'
        : 'You are measured on outcomes at your assigned facilities.',
      icon: Target,
      color: 'purple',
    },
    {
      title: employmentStatus === 'part-time'
        ? 'Your award ceiling is proportional to your schedule.'
        : 'Part-time employees have the potential to receive a proportional incentive.',
      message: employmentStatus === 'part-time'
        ? 'Your maximum award scales with your days worked per week. Same quality standards apply regardless of schedule.'
        : "Lower earning ceiling, same quality standards. Part-timers aren't penalized - they're scaled proportionally.",
      icon: Users,
      color: 'teal',
    },
  ];

  // When full-time: filter out the part-time message
  const messages = employmentStatus === 'full-time'
    ? baseMessages.filter((_, index) => index !== baseMessages.length - 1)
    : baseMessages;

  const colorClasses = {
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      icon: 'bg-green-100 text-green-600',
      title: 'text-green-800',
    },
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      icon: 'bg-blue-100 text-blue-600',
      title: 'text-blue-800',
    },
    purple: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      icon: 'bg-purple-100 text-purple-600',
      title: 'text-purple-800',
    },
    teal: {
      bg: 'bg-teal-50',
      border: 'border-teal-200',
      icon: 'bg-teal-100 text-teal-600',
      title: 'text-teal-800',
    },
  };

  return (
    <Section id="key-messages" background="gray">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Key Messages
      </h2>
      <p className="text-gray-600 mb-6">
        The key principles behind how EQuIP works for you.
      </p>

      <div className="space-y-3">
        {messages.map((msg) => {
          const colors = colorClasses[msg.color as keyof typeof colorClasses];
          return (
            <div
              key={msg.title}
              className={cn('flex items-start gap-4 p-4 rounded-xl border-l-4', colors.bg, colors.border)}
            >
              <div
                className={cn(
                  'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                  colors.icon
                )}
              >
                <msg.icon className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h3 className={cn('font-bold', colors.title)}>
                  {msg.title}
                </h3>
                <p className="text-gray-600 text-sm mt-0.5">{msg.message}</p>
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

// Next Steps Section
function NextStepsSection(): React.ReactElement {
  return (
    <Section id="next-steps">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Continue Your Preparation
      </h2>

      <div className="grid md:grid-cols-2 gap-4">
        <Link to="/physician/leader-guide">
          <Card className="h-full border-2 border-eventus-purple/20 hover:border-eventus-purple/50 hover:shadow-lg transition-all cursor-pointer group">
            <CardContent className="p-6 flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-xl bg-eventus-purple/10 flex items-center justify-center mb-4 group-hover:bg-eventus-purple transition-colors">
                <Book className="w-6 h-6 text-eventus-purple group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-eventus-navy mb-2">
                Leader Guide
              </h3>
              <p className="text-gray-600 text-sm mb-3">
                Talking points and expectations for team conversations.
              </p>
              <div className="flex items-center text-eventus-purple font-semibold text-sm">
                View Leader Guide
                <ChevronRight className="w-4 h-4 ml-1" />
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/physician/faq">
          <Card className="h-full border-2 border-eventus-teal/20 hover:border-eventus-teal/50 hover:shadow-lg transition-all cursor-pointer group">
            <CardContent className="p-6 flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-xl bg-eventus-teal/10 flex items-center justify-center mb-4 group-hover:bg-eventus-teal transition-colors">
                <HelpCircle className="w-6 h-6 text-eventus-teal group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-eventus-navy mb-2">FAQ</h3>
              <p className="text-gray-600 text-sm mb-3">
                Frameworks to help you answer the questions your team may ask.
              </p>
              <div className="flex items-center text-eventus-teal font-semibold text-sm">
                View FAQ
                <ChevronRight className="w-4 h-4 ml-1" />
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>
    </Section>
  );
}

// Main Component
interface PhysicianFrameworkProps {
  readOnly?: boolean;
  /** Override the calculator role when readOnly (e.g. 'psychiatry-md' for psychiatrist persona). Defaults to 'primary-care-md'. */
  lockedCalculatorRole?: import('@/store/equipStore').RoleType;
  employmentStatus?: EmploymentStatus;
  navItems?: { id: string; label: string; path: string; icon?: React.ReactNode }[];
  showPersonaDropdown?: boolean;
  showHome?: boolean;
}

export function PhysicianFramework({ readOnly = false, lockedCalculatorRole, employmentStatus, navItems, showPersonaDropdown, showHome }: PhysicianFrameworkProps): React.ReactElement {
  const visibleNavSections = readOnly
    ? navSections.filter(s => s.id !== 'next-steps')
    : navSections;

  return (
    <div className="min-h-screen bg-gray-50 pb-20 lg:pb-0">
      {/* Floating Navigation */}
      <FloatingNav items={navItems ?? [
        { id: 'framework', label: 'Framework', path: '/physician/framework' },
        { id: 'leader-guide', label: 'Leader Guide', path: '/physician/leader-guide' },
        { id: 'faq', label: 'FAQ', path: '/physician/faq' },
        { id: 'leadership', label: 'Leadership Dashboard', path: '/leadership' },
      ]} showPersonaDropdown={showPersonaDropdown} showHome={showHome} />

      {/* Overlay Side Navigation */}
      <OverlaySideNav sections={visibleNavSections} />

      {/* Hero Section */}
      <HeroSection readOnly={readOnly} />

      {/* Who Qualifies - First thing after hero */}
      <Section id="eligibility">
        <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-6">
          Who Qualifies for EQuIP
        </h2>
        <EligibilitySection
          eligibility={getPhysicianEligibility()}
          variant="physician"
          employmentStatus={employmentStatus}
        />
      </Section>

      {/* TIER 1: The 30-Second Scan */}
      <HowToEarnSection />

      {/* TIER 2: The Three Categories */}
      <EligibilityMeasureSection />
      <ConsistencyMeasuresSection />
      <OutcomeMeasuresSection />

      {/* Maximum Award */}
      <MaximumAwardSection employmentStatus={employmentStatus} />

      {/* Quarterly Calculation Breakdown */}
      <QuarterlyBreakdownSection />

      {/* Interactive Calculator */}
      <InteractiveCalculatorSection readOnly={readOnly} lockedCalculatorRole={lockedCalculatorRole} employmentStatus={employmentStatus} />

      {/* TIER 3: Detailed Sections (Collapsible) */}
      <PayoutScenariosSection employmentStatus={employmentStatus} />
      <MeasureDefinitionsSection />

      {/* Key Messages */}
      <KeyMessagesSection employmentStatus={employmentStatus} isLeadership={!readOnly} />

      {/* Next Steps — hidden in readOnly mode (links to leadership-only pages) */}
      {!readOnly && <NextStepsSection />}
    </div>
  );
}
